/**
 * Tracks texts the current operator just sent so WS echoes (often `dialog.updated`
 * without `from`) do not trigger visitor-message sounds or duplicate client bubbles.
 */

const TTL_MS = 60_000
const pendingByChat = new Map<string, TrackedOutgoing[]>()

type TrackedOutgoing = {
  text: string
  at: number
  hasMedia: boolean
  names: string[]
}

export type TrackOperatorOutgoingOptions = {
  hasMedia?: boolean
  attachmentNames?: string[]
}

function prune(chatId: string, now = Date.now()): TrackedOutgoing[] {
  const next = (pendingByChat.get(chatId) ?? []).filter((x) => now - x.at < TTL_MS)
  if (next.length === 0) pendingByChat.delete(chatId)
  else pendingByChat.set(chatId, next)
  return next
}

function normalizeEchoText(value: string): string {
  return String(value ?? '')
    .trim()
    .replace(/^[📎📄]\s*/u, '')
    .toLowerCase()
}

/** Backend lastMessage for media is often a generic label, not the file name. */
export function isAttachmentPreviewText(text: string): boolean {
  const normalized = normalizeEchoText(text)
  if (!normalized) return false
  return /^(вложени[ея]|attachment[s]?|file|файл|image|изображение|фото|photo|pdf)([.!?…]*)$/i.test(
    normalized,
  )
}

/** Call right before/when the operator sends a message. */
export function trackOperatorOutgoing(
  chatId: string,
  text: string,
  opts?: TrackOperatorOutgoingOptions,
): void {
  const id = String(chatId ?? '').trim()
  const trimmed = String(text ?? '').trim()
  const names = (opts?.attachmentNames ?? []).map((name) => String(name ?? '').trim()).filter(Boolean)
  const hasMedia = Boolean(opts?.hasMedia || names.length)
  if (!id || (!trimmed && !hasMedia)) return
  const now = Date.now()
  const next = prune(id, now)
  pendingByChat.set(id, [
    ...next,
    {
      text: trimmed,
      at: now,
      hasMedia,
      names,
    },
  ])
}

/** Drop a tracked outgoing (e.g. after send failure / optimistic rollback). */
export function untrackOperatorOutgoing(chatId: string, text: string, sentAt?: number): void {
  const id = String(chatId ?? '').trim()
  const trimmed = String(text ?? '').trim()
  if (!id || !trimmed) return
  const next = prune(id).filter(
    (x) => !(x.text === trimmed && (sentAt == null || x.at === sentAt)),
  )
  if (next.length === 0) pendingByChat.delete(id)
  else pendingByChat.set(id, next)
}

function trackedMatchesText(entry: TrackedOutgoing, text: string): boolean {
  const trimmed = String(text ?? '').trim()
  if (entry.text && entry.text === trimmed) return true
  const normalized = normalizeEchoText(trimmed)
  if (!normalized) return entry.hasMedia
  if (entry.names.some((name) => normalizeEchoText(name) === normalized)) return true
  return entry.hasMedia && isAttachmentPreviewText(trimmed)
}

/** True when this text matches a recent operator send for the chat. */
export function isOperatorOutgoingEcho(chatId: string, text: string): boolean {
  const id = String(chatId ?? '').trim()
  if (!id) return false
  const trimmed = String(text ?? '').trim()
  if (!trimmed && !hasRecentOperatorMediaEcho(id)) return false
  return prune(id).some((entry) => trackedMatchesText(entry, trimmed))
}

export function hasRecentOperatorMediaEcho(chatId: string): boolean {
  const id = String(chatId ?? '').trim()
  if (!id) return false
  return prune(id).some((entry) => entry.hasMedia)
}
